import { Amplify } from 'aws-amplify';
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';

// Custom error class for Cognito errors
class CognitoAuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CognitoAuthError';
  }
}

// Configure Amplify with Cognito settings
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID as string,
      userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID as string,
    },
  },
});

export interface CognitoUser {
  userId: string;
  email: string;
  username?: string;
  givenName?: string;
  familyName?: string;
  phoneNumber?: string;
  supplierId?: string;
  jobTitle?: string;
  preferredLanguage?: string;
  locale?: string;
  groups: string[];
}

export type SignInResult =
  | {
    status: 'success';
    user: CognitoUser;
  }
  | {
    status: 'newPasswordRequired';
    username: string;
  }
  | {
    status: 'mfaRequired';
    challengeName: string;
  };

/**
 * Sign in with email/username and password
 */
export async function cognitoSignIn(
  email: string,
  password: string
): Promise<SignInResult> {
  const result = await amplifySignIn({
    username: email,
    password,
    options: {authFlowType: 'USER_PASSWORD_AUTH',},
  });

  const { signInStep } = result.nextStep;

  if (signInStep === 'DONE') {
    const user = await getCurrentCognitoUser();
    if (!user) {
      throw new CognitoAuthError('Failed to get user after sign in');
    }
    // Verify user is a supplier user
    if (!user.supplierId) {
      await amplifySignOut();
      throw new CognitoAuthError(
        'Access denied. This portal is for supplier users only.'
      );
    }
    return {
      status: 'success',
      user 
    };
  }

  if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
    return {
      status: 'newPasswordRequired',
      username: email 
    };
  }

  if (
    signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' ||
    signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE'
  ) {
    return {
      status: 'mfaRequired',
      challengeName: signInStep 
    };
  }

  if (signInStep === 'RESET_PASSWORD') {
    throw new CognitoAuthError(
      'Password reset required. Please check your email.'
    );
  }

  throw new CognitoAuthError(`Unexpected sign-in step: ${signInStep}`);
}

/**
 * Complete the new password challenge (first login)
 */
export async function completeNewPassword(
  newPassword: string
): Promise<CognitoUser> {
  const result = await confirmSignIn({ challengeResponse: newPassword });

  if (result.nextStep.signInStep !== 'DONE') {
    throw new CognitoAuthError(
      `Unexpected step after password change: ${result.nextStep.signInStep}`
    );
  }

  const user = await getCurrentCognitoUser();
  if (!user) {
    throw new CognitoAuthError('Failed to get user after password change');
  }

  // Verify user is a supplier user
  if (!user.supplierId) {
    await amplifySignOut();
    throw new CognitoAuthError(
      'Access denied. This portal is for supplier users only.'
    );
  }

  return user;
}

/**
 * Complete MFA challenge
 */
export async function completeMfaChallenge(code: string): Promise<CognitoUser> {
  const result = await confirmSignIn({ challengeResponse: code });

  if (result.nextStep.signInStep !== 'DONE') {
    throw new CognitoAuthError(
      `Unexpected step after MFA: ${result.nextStep.signInStep}`
    );
  }

  const user = await getCurrentCognitoUser();
  if (!user) {
    throw new CognitoAuthError('Failed to get user after MFA');
  }

  return user;
}

/**
 * Sign out the current user
 */
export async function cognitoSignOut(): Promise<void> {
  await amplifySignOut();
}

/**
 * Get the currently authenticated user with all attributes
 */
export async function getCurrentCognitoUser(): Promise<CognitoUser | null> {
  try {
    const user = await getCurrentUser();
    const session = await fetchAuthSession();
    const attributes = await fetchUserAttributes();

    const groups =
      (session.tokens?.idToken?.payload?.['cognito:groups'] as string[]) ?? [];

    return {
      userId: user.userId,
      email: attributes.email ?? '',
      username: user.username,
      givenName: attributes.given_name,
      familyName: attributes.family_name,
      phoneNumber: attributes.phone_number,
      supplierId: attributes['custom:supplierId'],
      jobTitle: attributes['custom:jobTitle'],
      preferredLanguage: attributes['custom:preferredLanguage'],
      locale: attributes.locale,
      groups,
    };
  } catch {
    return null;
  }
}

/**
 * Get the current access token for API calls
 */
export async function getAccessToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() ?? null;
  } catch {
    return null;
  }
}

/**
 * Request a password reset code
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await resetPassword({ username: email });
}

/**
 * Confirm password reset with code and new password
 */
export async function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  await confirmResetPassword({
    username: email,
    confirmationCode: code,
    newPassword,
  });
}
